import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import {
  getAppointmentColumns,
  APPOINTMENT_HIDDEN_COLUMNS,
} from '@/components/appointments/appointment-columns'
import {
  AppointmentToolbar,
  type DatePreset,
} from '@/components/appointments/appointment-toolbar'
import { AppointmentSummaryCards } from '@/components/appointments/appointment-summary-cards'
import { AppointmentSheet } from '@/components/appointments/appointment-sheet'
import { useAppointments, useAppointmentFilters } from '@/hooks/useAppointments'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import type {
  Appointment,
  AppointmentAttribution,
  AppointmentStatus,
  AppointmentView,
  PatientVisit,
} from '@/lib/api-client'
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table'
import { formatCurrency } from '@/lib/utils'

export const Route = createFileRoute('/appointments')({
  component: AppointmentsPage,
})

const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

const OUTCOME_STYLE: Record<PatientVisit['outcome'], { bg: string; text: string }> = {
  attended: { bg: '#10b981', text: 'white' },
  likely_redemption: { bg: '#3b82f6', text: 'white' },
  no_show: { bg: '#f59e0b', text: 'white' },
  cancelled: { bg: '#ef4444', text: 'white' },
  mixed: { bg: '#6b7280', text: 'white' },
}
const OUTCOME_LABEL: Record<PatientVisit['outcome'], string> = {
  attended: 'Attended',
  likely_redemption: 'Redemption',
  no_show: 'No-show',
  cancelled: 'Cancelled',
  mixed: 'Mixed',
}

/** Convert DatePreset to from/to YYYY-MM-DD strings (MYT). */
function presetToRange(preset: DatePreset, customFrom: string, customTo: string): { from?: string; to?: string } {
  if (preset === 'all') return {}
  if (preset === 'custom') return { from: customFrom || undefined, to: customTo || undefined }
  // MYT-aware: format current MYT date and roll from there. Using en-CA locale
  // gives us the YYYY-MM-DD format directly.
  const myt = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }))
  const ymd = (d: Date) => d.toLocaleDateString('en-CA')
  if (preset === 'today') return { from: ymd(myt), to: ymd(myt) }
  if (preset === 'tomorrow') {
    const t = new Date(myt)
    t.setDate(t.getDate() + 1)
    return { from: ymd(t), to: ymd(t) }
  }
  if (preset === 'last7days') {
    // Rolling: yesterday + week ahead. Avoids "This Week" Mon–Sun boundary
    // problem where Sunday's last-week bookings get cut off (Avixo schedules
    // future appointments, so a backward-only window also misses them).
    const start = new Date(myt)
    start.setDate(start.getDate() - 1)
    const end = new Date(myt)
    end.setDate(end.getDate() + 6)
    return { from: ymd(start), to: ymd(end) }
  }
  if (preset === 'month') {
    const first = new Date(myt.getFullYear(), myt.getMonth(), 1)
    const last = new Date(myt.getFullYear(), myt.getMonth() + 1, 0)
    return { from: ymd(first), to: ymd(last) }
  }
  return {}
}

function AppointmentsPage() {
  const { branchId } = useBranch()
  const navigate = useNavigate()
  const branchIdNum = BRANCH_IDS[branchId]

  // ---- Filter state ----
  // Defaults updated 28 Apr 2026 after operator review: "This Week" Mon–Sun
  // cut off Sunday bookings + DNTU-only-status hid 87% of data + Rows view
  // misanswered the boss's primary "berapa patient?" question. New defaults:
  //   - last7days (rolling window, yesterday + week ahead)
  //   - all 3 statuses (DNTU stays visually muted via badge)
  //   - patients view (per-patient-day flow, matches mental model)
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [datePreset, setDatePreset] = React.useState<DatePreset>('last7days')
  const [customFrom, setCustomFrom] = React.useState('')
  const [customTo, setCustomTo] = React.useState('')
  const [statuses, setStatuses] = React.useState<AppointmentStatus[]>([
    'turn_up',
    'cancelled',
    'did_not_turn_up',
  ])
  const [attribution, setAttribution] = React.useState<AppointmentAttribution>('all')
  const [creator, setCreator] = React.useState<string | null>(null)
  const [doctor, setDoctor] = React.useState<string | null>(null)
  const [view, setView] = React.useState<AppointmentView>('patients')
  const [includeTasks, setIncludeTasks] = React.useState(false)

  // ---- Table state ----
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(50)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    Object.fromEntries(APPOINTMENT_HIDDEN_COLUMNS.map((c) => [c, false])),
  )

  // ---- Sheet state ----
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [selectedId, setSelectedId] = React.useState<number | null>(null)

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // Reset page on filter changes
  React.useEffect(() => {
    setPage(1)
  }, [branchId, datePreset, customFrom, customTo, statuses, attribution, creator, doctor, view, includeTasks])

  const range = presetToRange(datePreset, customFrom, customTo)
  const sortBy = sorting[0]?.id as 'appointmentAt' | 'createdBy' | 'doctor' | 'status' | 'patientName' | undefined
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  const { data, isLoading } = useAppointments({
    branchId: branchIdNum,
    from: range.from,
    to: range.to,
    status: statuses.length > 0 ? statuses.join(',') : undefined,
    attribution,
    creator: creator ?? undefined,
    doctor: doctor ?? undefined,
    search: debouncedSearch || undefined,
    view,
    includeTasks,
    page,
    pageSize,
    sortBy,
    sortOrder: sorting.length > 0 ? sortOrder : undefined,
  })

  const { data: filtersData } = useAppointmentFilters(branchIdNum)

  const handleView = React.useCallback((apt: Appointment) => {
    setSelectedId(apt.id)
    setSheetOpen(true)
  }, [])

  const handleViewPatient = React.useCallback(
    (patientId: number) => navigate({ to: '/patients/$id', params: { id: String(patientId) } }),
    [navigate],
  )

  const columns = React.useMemo(
    () => getAppointmentColumns({ onView: handleView, onViewPatient: handleViewPatient }),
    [handleView, handleViewPatient],
  )

  // Rows table — only used in Rows view. Read from response shape (data.view)
  // not local state, so we don't try to render mismatched data while the query
  // refetches after a view toggle (keepPreviousData holds stale rows briefly).
  const responseView = data?.view
  const tableData: Appointment[] = data?.view === 'rows' ? (data.data as Appointment[]) : []
  const table = useReactTable({
    data: tableData,
    columns,
    pageCount: data?.pageCount ?? -1,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater
      setPage(next.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const subtitle = data
    ? view === 'rows'
      ? `${data.total.toLocaleString()} appointments${includeTasks ? ' (incl. tasks)' : ''}`
      : `${data.total.toLocaleString()} patient-day rows`
    : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header title="Appointments" subtitle={subtitle} />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4 overflow-y-auto">
        <AppointmentSummaryCards data={data} isLoading={isLoading} />

        <AppointmentToolbar
          search={search}
          onSearchChange={setSearch}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          statuses={statuses}
          onStatusesChange={setStatuses}
          attribution={attribution}
          onAttributionChange={setAttribution}
          creator={creator}
          onCreatorChange={setCreator}
          doctor={doctor}
          onDoctorChange={setDoctor}
          view={view}
          onViewChange={setView}
          includeTasks={includeTasks}
          onIncludeTasksChange={setIncludeTasks}
          filtersData={filtersData}
          table={table}
        />

        <FilterBanner
          total={data?.total}
          unfilteredTotal={data?.unfilteredTotal}
          onClearFilters={() => {
            // "Show everything in scope" — clears every user-controlled filter
            // including date. Banner disappears (total === unfilteredTotal).
            // includeTasks stays at user choice (it's scope, not a user filter).
            setSearch('')
            setDatePreset('all')
            setCustomFrom('')
            setCustomTo('')
            setStatuses(['turn_up', 'cancelled', 'did_not_turn_up'])
            setAttribution('all')
            setCreator(null)
            setDoctor(null)
          }}
          onShowAllTime={() => {
            setDatePreset('all')
            setCustomFrom('')
            setCustomTo('')
          }}
        />

        {view === 'rows' ? (
          <DataTable
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            pageCount={data?.pageCount}
            pageIndex={page - 1}
            pageSize={pageSize}
            onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
            sorting={sorting}
            onSortingChange={setSorting}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onRowClick={handleView}
            emptyMessage="No appointments. Adjust filters or upload Avixo data."
          />
        ) : (
          <PatientsViewTable
            visits={data?.view === 'patients' ? (data.data as PatientVisit[]) : []}
            isLoading={isLoading || responseView !== 'patients'}
          />
        )}
      </div>

      <AppointmentSheet
        appointmentId={selectedId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  )
}

// ============================================================================
// FILTER BANNER — surface "X of Y rows hidden" when defaults hide >30% of scope
// ============================================================================

function FilterBanner({
  total,
  unfilteredTotal,
  onClearFilters,
  onShowAllTime,
}: {
  total: number | undefined
  unfilteredTotal: number | undefined
  onClearFilters: () => void
  onShowAllTime: () => void
}) {
  if (total === undefined || unfilteredTotal === undefined) return null
  if (unfilteredTotal === 0) return null
  // Threshold: hide when filters trim less than 30% (banner would be noise
  // when filter is intentional/light)
  const visibleRatio = total / unfilteredTotal
  if (visibleRatio >= 0.7) return null

  const hidden = unfilteredTotal - total
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-info/30 bg-info/10 px-3 py-2 text-[13px]">
      <span className="text-text-dark">
        Showing <strong>{total.toLocaleString()}</strong> of{' '}
        <strong>{unfilteredTotal.toLocaleString()}</strong> rows
        <span className="text-text-muted"> · {hidden.toLocaleString()} hidden by current filters</span>
      </span>
      <div className="flex items-center gap-2">
        <button
          onClick={onShowAllTime}
          className="rounded-full bg-card border border-border px-3 py-1 text-[12px] hover:bg-hover"
        >
          Show all time
        </button>
        <button
          onClick={onClearFilters}
          className="rounded-full bg-info text-white px-3 py-1 text-[12px] hover:bg-info/90"
        >
          Clear filters
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// PATIENTS VIEW — collapsed per-patient-day flow rendered as cards/table
// ============================================================================

function PatientsViewTable({ visits, isLoading }: { visits: PatientVisit[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
        Loading patient visits...
      </div>
    )
  }
  if (visits.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
        No patient visits in the selected window.
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-background/50 border-b border-border">
            <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Patient</th>
              <th className="px-3 py-2.5">Slots</th>
              <th className="px-3 py-2.5">Outcome</th>
              <th className="px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5">Doctors / Reasons</th>
              <th className="px-3 py-2.5 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((v) => {
              const style = OUTCOME_STYLE[v.outcome] ?? OUTCOME_STYLE.mixed
              // Defensive: array fields may come back null/undefined from neon-http
              // for edge-case rows. Default to empty array everywhere.
              const slots = v.slots ?? []
              const creators = (v.creators ?? []).filter(Boolean) as string[]
              const doctors = (v.doctors ?? []).filter(Boolean) as string[]
              const reasons = (v.reasons ?? []).filter(Boolean) as string[]
              const firstSlot = slots[0]?.split(' ')?.[1]
              const lastSlot = slots[slots.length - 1]?.split(' ')?.[1]
              return (
                <tr key={`${v.branchId}-${v.patientKey}-${v.visitDate}`} className="border-b border-border last:border-0 hover:bg-hover/30">
                  <td className="px-3 py-3 align-top text-[13px] font-medium">{v.visitDate}</td>
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-[13px]">{v.patientName ?? <span className="italic text-text-muted">(unknown)</span>}</div>
                    {v.pno && <div className="text-xs text-text-muted">{v.pno}</div>}
                    {v.patientType && (
                      <Badge variant="outline" className="text-[10px] mt-1">{v.patientType}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="text-[13px] font-mono">{v.slotCount}×</div>
                    <div className="text-xs text-text-muted">
                      {firstSlot ?? ''}
                      {slots.length > 1 && lastSlot ? ` → ${lastSlot}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <Badge style={{ background: style.bg, color: style.text }}>
                      {OUTCOME_LABEL[v.outcome] ?? v.outcome ?? 'unknown'}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className="text-[13px]">{v.attribution === 'closer' ? '🎯 Closer' : '🏥 In-clinic'}</span>
                    <div className="text-xs text-text-muted truncate max-w-[160px]" title={creators.join(', ')}>
                      {[...new Set(creators)].join(', ')}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="text-xs text-text-dark truncate max-w-[200px]" title={doctors.join(', ')}>
                      {[...new Set(doctors)].join(' → ')}
                    </div>
                    <div className="text-xs text-text-muted truncate max-w-[200px]" title={reasons.join(', ')}>
                      {[...new Set(reasons)].join(', ')}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top text-right text-[13px] font-medium">
                    {parseFloat(v.totalInvoice) > 0 ? formatCurrency(parseFloat(v.totalInvoice)) : <span className="text-text-muted">-</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
